import { z } from 'zod';
import { LIFECYCLE_STATUS, ZusammenfuehrungSchema } from './auftrag-zusammenfuehren.js';
import { IsoDateTimeSchema, ProjectKeySchema, SegmentSchema, UuidSchema } from './common.js';
import { ProjectReminderSchema } from './notification.js';
import { TagSchema } from './tag.js';

export const HandoverReadyFieldsSchema = z.object({
  restbudget: z.string().optional(),
  folgethemen: z.string().optional(),
  debriefing: z.string().optional(),
  uebergabe_an_service: z.string().optional(),
  // #119: Notiz der kaufm. Leitung; befüllen setzt den KFML-Worklist-Haken, kein Pflichtfeld
  kfml_notiz: z.string().optional(),
});
export type HandoverReadyFields = z.infer<typeof HandoverReadyFieldsSchema>;

export const ResponsibilitySchema = z.enum(['PU', 'Service', 'MTB']);
/**
 * Dienstleistungsart am Auftrag (frueher „ms-Klassifikation"/„Sparte", Beschriftung
 * vereinheitlicht in #80). Kommt aus Salesforce, wird nie im Tool gesetzt.
 */
export const MsClassificationSchema = z.enum(['msPeople', 'msSolution', 'msService']);
export type Responsibility = z.infer<typeof ResponsibilitySchema>;

// Strukturierter Intake-Block (Issue #14): diskrete Schlüssel je Eingabefeld,
// alle .partial() → rückwärtskompatibel zu Inhalten ohne `uebergabe_intake`.
// #83: `ms_pl`/`fbl` entfallen (PL/Manager sind Picker am Projekt und gehen nach SF);
// Zod strippt die Altschlüssel beim Parsen, jedes Speichern räumt sie also mit ab.
// `mb_ansprechpartner` = interner MA fürs Mission Briefing (Label „Mission Briefing").
export const UebergabeIntakeSchema = z
  .object({
    vertrieb: z.string(),
    fachl_ap: z.string(),
    sparte: z.string(),
    /**
     * ALTBESTAND (#116): Freitext von vor der Personenauswahl. Wird nur noch GELESEN (Hinweis
     * „bisher: …" und KFML-Todo-Fallback) und nach dem ersten erfolgreichen Entra-Abgleich von
     * `ueberfuehreQmAltbestand` entfernt. Nicht mehr schreiben.
     */
    qm: z.string(),
    /** QM Verantwortlicher als Person (#116). `null` = bewusst geleert; Schlüssel fehlt = nie gesetzt. */
    qm_user_id: z.string().uuid().nullable(),
    mb_ansprechpartner: z.string(),
    sonstiges: z.string(),
    updated_at: z.string(),
    updated_by: z.string(),
  })
  .partial();
export type UebergabeIntake = z.infer<typeof UebergabeIntakeSchema>;

export const IntakeHandoverFieldsSchema = z.object({
  presales_notes: z.string().optional(),
  angebot_link: z.string().optional(),
  angebot_besonderheiten: z.string().optional(),
  intake_meeting_notes: z.string().optional(),
  uebergabe_intake: UebergabeIntakeSchema.optional(),
});
export type IntakeHandoverFields = z.infer<typeof IntakeHandoverFieldsSchema>;

/**
 * #158: Übergabe-Notiz aus der Staffing-Maske — schreibt NUR `uebergabe_intake.sonstiges`
 * (`PATCH /v1/projects/:id/uebergabe-notiz`). Der Server führt zusammen; andere Intake-Felder
 * bleiben unberührt. Leer/nur Leerzeichen/`null` entfernt den Schlüssel. `strict`: ein Aufruf,
 * der weitere Intake-Felder mitschickt, ist ein Fehler — dafür gibt es den Projekt-PATCH.
 */
export const UebergabeNotizInputSchema = z
  .object({
    sonstiges: z.string().max(20_000).nullable(),
  })
  .strict();
export type UebergabeNotizInput = z.infer<typeof UebergabeNotizInputSchema>;

export const StaffingDetailsSchema = z.object({
  rollen: z.array(z.string()).optional(),
  skills: z.array(z.string()).optional(),
  start_gewuenscht: IsoDateTimeSchema.optional(),
  kommentar: z.string().optional(),
  owner: z.string().optional(),
});
export type StaffingDetails = z.infer<typeof StaffingDetailsSchema>;

export const ProjectSchema = z.object({
  project_id: UuidSchema,
  project_key: ProjectKeySchema,
  salesforce_object_id: z.string().min(1),
  name: z.string().min(1),
  customer_name: z.string().min(1),
  segment: SegmentSchema,
  bereich: z.string().optional(),
  phase_id: UuidSchema,
  phase_changed_at: IsoDateTimeSchema,
  phase_changed_by: UuidSchema.optional(),
  project_lead_user_id: UuidSchema.nullable().optional(),
  manager_user_id: UuidSchema.nullable().optional(),
  mtb_user_id: UuidSchema.nullable().optional(),
  staffing_needed: z.boolean().default(false),
  staffing_details: StaffingDetailsSchema.optional(),
  // „Staffing abgeschlossen" (Staffing neu, Issue #1): gesetzt = Projekt aus KL-Liste raus.
  staffing_completed_at: IsoDateTimeSchema.nullable().optional(),
  staffing_completed_by: UuidSchema.nullable().optional(),
  links: z.record(z.string(), z.string().url()).optional(),
  handover_ready_fields: HandoverReadyFieldsSchema.optional(),
  responsibility: ResponsibilitySchema.nullable().optional(),
  ms_classification: MsClassificationSchema.nullable().optional(),
  due_date: z.string().date().nullable().optional(),
  // KL-Wiedervorlage „Wieder fällig am" (tool-intern, manuell; KEINE Reminder-Mails,
  // unabhängig von due_date) — Staffing-neu (Issue #1).
  kl_followup_date: z.string().date().nullable().optional(),
  // #117: Block-Zustand, wie ihn projects-with-lead.ts liefert — die Übergabe sperrt damit den
  // Phasenwechsel-Knopf.
  is_blocked: z.boolean().optional(),
  blocked_reason: z.string().nullable().optional(),
  // KFML-Worklist (PV-016): manueller Haken der Kaufm. Leitung. NICHT die KFML-Todoliste
  // (Issue #14 — LLM-Extraktion aus Intake-Notizen, kein Feld am Projekt).
  kfml_worklist: z.boolean().optional(),
  kfml_worklist_flagged_at: IsoDateTimeSchema.nullable().optional(),
  kfml_worklist_flagged_by: UuidSchema.nullable().optional(),
  // PV-052 L2: „final geschlossen" (A7). von = null → automatisch bzw. Altbestand.
  final_geschlossen_am: IsoDateTimeSchema.nullable().optional(),
  final_geschlossen_von: UuidSchema.nullable().optional(),
  final_geschlossen_grund: z.string().nullable().optional(),
  final_altbestand: z.boolean().optional(),
  intake_handover_fields: IntakeHandoverFieldsSchema.nullable().optional(),
  /**
   * Steht das Projekt in der Eingangsphase? Quelle ist `phases.is_intake`, NICHT der
   * Phasenname. Traegt den Zuteilungsschutz aus #78: Dort duerfen Projektleiter und Manager
   * nur von der kaufmaennischen Leitung geaendert werden.
   */
  phase_is_intake: z.boolean().optional(),
  /**
   * Steht das Projekt in der Uebergabephase? Quelle ist `phases.is_handover`, NICHT der
   * Phasenname. Traegt die Abschlussregel aus #93: Ausserhalb von Handover ist beim
   * Abschluss eine Begruendung Pflicht — der Dialog kennzeichnet das Feld anhand dieses
   * Werts, bevor der Server mit 422 antwortet.
   */
  phase_is_handover: z.boolean().optional(),
  /** Bestehende Erinnerung (ueberfaellig / steht an / Uebergabefelder leer), serverseitig abgeleitet. */
  reminder: ProjectReminderSchema.nullable().optional(),
  /**
   * Ausstehende Uebertragung nach Salesforce (#78), je Thema. Nur gesetzt, wenn wirklich
   * etwas aussteht — `undefined`/leer heisst „alles zugestellt".
   *
   * `visible` entscheidet ueber die Darstellung: darunter ein dezenter Hinweis („wird
   * uebertragen"), darueber eine Warnung mit Verwerfen-Moeglichkeit. Die Schwelle liegt
   * bewusst im Backend, damit Anzeige und Wiederholungslogik nicht auseinanderlaufen.
   */
  sf_write_pending: z
    .array(
      z.object({
        topic: z.string(),
        label: z.string(),
        since: IsoDateTimeSchema,
        attempts: z.number().int(),
        visible: z.boolean(),
        error_kind: z.enum(['retryable', 'permanent']).nullable().optional(),
        error_message: z.string().nullable().optional(),
        last_attempt_at: IsoDateTimeSchema.nullable().optional(),
      }),
    )
    .optional(),
  lifecycle_status: z.enum(LIFECYCLE_STATUS).default('active'),
  // #123: nur im Einzel-GET gefüllt.
  zusammenfuehrung: ZusammenfuehrungSchema.optional(),
  completed_at: IsoDateTimeSchema.nullable().optional(),
  completed_by: UuidSchema.nullable().optional(),
  completion_reason: z.string().nullable().optional(),
  last_sync_at: IsoDateTimeSchema.optional(),
  last_sync_status: z.enum(['success', 'partial', 'failure']).optional(),
  salesforce_url: z.string().url().nullable().optional(),
  salesforce_last_modified_at: IsoDateTimeSchema.nullable().optional(),
  salesforce_data: z
    .object({
      projektstatus: z.string().nullable().optional(),
      sparte: z.string().nullable().optional(),
      abrechnungsart: z.string().nullable().optional(),
      budget: z.number().nullable().optional(),
      // PV-052 L2: SF-Projektstart/-ende (Karte „Salesforce-Daten"). MUSS hier stehen — Zod
      // entfernt unbekannte Felder still.
      projektende: z.string().date().nullable().optional(),
      projektstart: z.string().date().nullable().optional(),
      projektnotizen: z.string().nullable().optional(),
      // Account-Referenz (Issue #71) — Quelle der `account_id` für die
      // Orakel-Abfrage `get_account_360`. MUSS hier stehen: Zod entfernt
      // unbekannte Felder stillschweigend, das Feld käme sonst aus dem Sync und
      // wäre nach dem ersten Schema-Durchlauf spurlos weg.
      account: z.object({ id: z.string(), name: z.string() }).nullable().optional(),
      opportunity: z.object({ id: z.string(), name: z.string() }).nullable().optional(),
      qm_kontakt: z.object({ id: z.string(), name: z.string() }).nullable().optional(),
      endkunde: z.object({ id: z.string(), name: z.string() }).nullable().optional(),
      // Vorbelegungs-Quellen für den Intake-Block (Issue #14).
      opportunity_owner: z.string().optional(),
      fachlicher_ansprechpartner: z.string().optional(),
    })
    .nullable()
    .optional(),
  created_at: IsoDateTimeSchema,
  updated_at: IsoDateTimeSchema,
  tags: z.array(TagSchema).optional(),
});
export type Project = z.infer<typeof ProjectSchema>;

export const CreateProjectInputSchema = ProjectSchema.omit({
  project_id: true,
  phase_changed_at: true,
  created_at: true,
  updated_at: true,
});
export type CreateProjectInput = z.infer<typeof CreateProjectInputSchema>;

export const UpdateProjectInputSchema = ProjectSchema.partial().required({
  project_id: true,
});
export type UpdateProjectInput = z.infer<typeof UpdateProjectInputSchema>;

export const MovePhaseInputSchema = z.object({
  project_id: UuidSchema,
  to_phase_id: UuidSchema,
});
export type MovePhaseInput = z.infer<typeof MovePhaseInputSchema>;

export const CompleteProjectInputSchema = z.object({
  reason: z.string().min(1).max(2000).optional(),
});
export type CompleteProjectInput = z.infer<typeof CompleteProjectInputSchema>;

export const ReactivateProjectInputSchema = z.object({
  reason: z.string().min(1).max(2000).optional(),
});
export type ReactivateProjectInput = z.infer<typeof ReactivateProjectInputSchema>;
